import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { brl, fmtDateBR, fmtTimeBR, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, X, Check } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const STATUS_LABEL = {
  agendado: "Agendado", confirmado: "Confirmado", em_atendimento: "Em atendimento",
  concluido: "Concluído", cancelado: "Cancelado", nao_compareceu: "Não compareceu",
};
const STATUS_CLASS = {
  agendado: "bg-[#F5ECE8] text-[#682F3E]",
  confirmado: "bg-[#EBF2EE] text-[#4F7A61]",
  em_atendimento: "bg-[#E0F2FE] text-[#0369A1]",
  concluido: "bg-[#F3E8FF] text-[#6B21A8]",
  cancelado: "bg-[#FEE2E2] text-[#991B1B]",
  nao_compareceu: "bg-[#F9ECE8] text-[#B76E56]",
};

export default function Agenda() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(null);
  const [params] = useSearchParams();

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/appointments", { params: { start: todayISO() } });
      setItems(r.data);
    } catch (e) {
      console.error("Não foi possível atualizar a agenda.", e);
    }
  }, []);

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data));
    api.get("/services").then(r => setServices(r.data));
    if (params.get("new") === "1") setShowModal(true);
  }, [params]);

  useEffect(() => {
    const refresh = () => { void reload(); };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };

    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = window.setInterval(refresh, 30000);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [reload]);

  const groups = items.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="agenda-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Agenda</h1>
          <p className="text-[#71717A] text-sm mt-1">Seus próximos atendimentos.</p>
        </div>
        <button data-testid="btn-new-appt" onClick={() => setShowModal(true)} className="btn-primary-nc flex items-center gap-1 text-sm">
          <Plus size={16} /> Novo
        </button>
      </div>

      {Object.keys(groups).length === 0 ? (
        <EmptyState msg="Você ainda não tem atendimentos." onAction={() => setShowModal(true)} />
      ) : (
        Object.entries(groups).sort().map(([date, list]) => (
          <div key={date}>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#682F3E] mb-2">{fmtDateBR(date)}</div>
            <div className="space-y-2">
              {list.map(a => (
                <div key={a.id} data-testid={`appt-${a.id}`} className="card-nc p-4 flex items-center gap-3">
                  <div className="font-mono-brand text-sm font-bold text-[#682F3E] w-14">{fmtTimeBR(a.time)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#27272A] truncate">{a.client_name}</div>
                    <div className="text-xs text-[#71717A]">{a.service_name} · {a.duration_minutes} min</div>
                    <span className={`badge-status mt-1.5 ${STATUS_CLASS[a.status]}`}>{STATUS_LABEL[a.status]}</span>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="font-mono-brand text-sm font-semibold">{brl(a.final_price ?? a.price)}</span>
                    {a.status !== "concluido" && a.status !== "cancelado" && (
                      <button
                        data-testid={`btn-complete-${a.id}`}
                        onClick={() => setCompleteModal(a)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#EBF2EE] text-[#365F48] text-xs font-bold hover:bg-[#DDEBE3] transition-colors"
                      >
                        <Check size={14} /> Dar baixa
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {showModal && (
        <ApptModal
          clients={clients}
          services={services}
          onClientCreated={(client) => setClients(prev => [...prev, client])}
          onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await reload(); toast.success("Agendamento criado!"); }}
        />
      )}
      {completeModal && (
        <CompleteModal appt={completeModal} onClose={() => setCompleteModal(null)}
          onSaved={async () => { setCompleteModal(null); await reload(); toast.success("Atendimento baixado e registrado no financeiro!"); }} />
      )}
    </div>
  );
}

function EmptyState({ msg, onAction }) {
  return (
    <div className="card-nc p-8 text-center" data-testid="agenda-empty">
      <p className="text-[#71717A] mb-3">{msg}</p>
      <button onClick={onAction} className="btn-primary-nc">Novo agendamento</button>
    </div>
  );
}

function ApptModal({ clients, services, onClientCreated, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_id: clients[0]?.id || "", service_id: services[0]?.id || "",
    date: todayISO(), time: "10:00", notes: "", status: "agendado",
  });
  const [loading, setLoading] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", phone: "", whatsapp: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const saveClient = async () => {
    if (!newClient.name.trim()) {
      toast.error("Informe o nome da cliente.");
      return;
    }
    setSavingClient(true);
    try {
      const payload = {
        name: newClient.name.trim(),
        phone: newClient.phone.trim() || null,
        whatsapp: newClient.whatsapp.trim() || null,
      };
      const { data } = await api.post("/clients", payload);
      onClientCreated?.(data);
      setForm(prev => ({ ...prev, client_id: data.id }));
      setNewClient({ name: "", phone: "", whatsapp: "" });
      setShowNewClient(false);
      toast.success("Cliente cadastrada e selecionada!");
    } catch (e) {
      toast.error(apiError(e, "Não foi possível cadastrar a cliente."));
    } finally {
      setSavingClient(false);
    }
  };

  const save = async () => {
    if (!form.client_id || !form.service_id) { toast.error("Selecione cliente e serviço."); return; }
    setLoading(true);
    try { await api.post("/appointments", form); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Novo agendamento" testid="modal-appt">
      <Field label="Cliente">
        <select data-testid="appt-client" value={form.client_id} onChange={set("client_id")} className="input-nc">
          <option value="">Selecione…</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setShowNewClient(v => !v)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#682F3E]"
          data-testid="appt-new-client"
        >
          <Plus size={14} /> {showNewClient ? "Fechar cadastro rápido" : "Cadastrar nova cliente"}
        </button>
      </Field>

      {showNewClient && (
        <div className="rounded-2xl border border-[#E4E4E7] bg-[#FAFAFA] p-3 space-y-3">
          <div>
            <div className="text-sm font-semibold text-[#27272A]">Cadastro rápido</div>
            <div className="text-xs text-[#71717A]">Cadastre sem sair do agendamento.</div>
          </div>
          <Field label="Nome*">
            <input
              data-testid="quick-client-name"
              value={newClient.name}
              onChange={e => setNewClient({ ...newClient, name: e.target.value })}
              className="input-nc"
              placeholder="Nome da cliente"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone">
              <input
                data-testid="quick-client-phone"
                value={newClient.phone}
                onChange={e => setNewClient({ ...newClient, phone: e.target.value })}
                className="input-nc"
              />
            </Field>
            <Field label="WhatsApp">
              <input
                data-testid="quick-client-whatsapp"
                value={newClient.whatsapp}
                onChange={e => setNewClient({ ...newClient, whatsapp: e.target.value })}
                className="input-nc"
              />
            </Field>
          </div>
          <button
            type="button"
            onClick={saveClient}
            disabled={savingClient}
            className="w-full h-10 rounded-xl border border-[#682F3E] text-[#682F3E] text-sm font-bold"
          >
            {savingClient ? "Cadastrando…" : "Cadastrar e selecionar"}
          </button>
        </div>
      )}

      <Field label="Serviço">
        <select data-testid="appt-service" value={form.service_id} onChange={set("service_id")} className="input-nc">
          <option value="">Selecione…</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name} — {brl(s.price)}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Data"><input data-testid="appt-date" type="date" value={form.date} onChange={set("date")} className="input-nc" /></Field>
        <Field label="Hora"><input data-testid="appt-time" type="time" value={form.time} onChange={set("time")} className="input-nc" /></Field>
      </div>
      <Field label="Observações">
        <textarea data-testid="appt-notes" value={form.notes} onChange={set("notes")} rows={2} className="input-nc" />
      </Field>
      <button data-testid="appt-save" onClick={save} disabled={loading} className="btn-primary-nc w-full">
        {loading ? "Salvando…" : "Salvar agendamento"}
      </button>
    </Modal>
  );
}

function CompleteModal({ appt, onClose, onSaved }) {
  const [form, setForm] = useState({ final_price: appt.price || 0, payment_method: "pix", paid: "sim" });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { await api.post(`/appointments/${appt.id}/complete`, form); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title="Dar baixa no atendimento" testid="modal-complete">
      <p className="text-sm text-[#71717A]">
        Confirme o valor e o pagamento para concluir o atendimento e registrar a movimentação no financeiro.
      </p>
      <Field label="Valor final (R$)">
        <input data-testid="complete-price" type="number" step="0.01" value={form.final_price}
          onChange={e => setForm({ ...form, final_price: Number(e.target.value) })} className="input-nc" />
      </Field>
      <Field label="Forma de pagamento">
        <select data-testid="complete-method" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className="input-nc">
          <option value="pix">Pix</option><option value="dinheiro">Dinheiro</option>
          <option value="debito">Débito</option><option value="credito">Crédito</option>
          <option value="outro">Outro</option>
        </select>
      </Field>
      <Field label="Foi pago?">
        <select data-testid="complete-paid" value={form.paid} onChange={e => setForm({ ...form, paid: e.target.value })} className="input-nc">
          <option value="sim">Sim</option><option value="parcial">Parcial</option><option value="nao">Não</option>
        </select>
      </Field>
      <button data-testid="complete-save" onClick={save} disabled={loading} className="btn-primary-nc w-full flex items-center justify-center gap-2">
        <Check size={16} /> {loading ? "Salvando…" : "Dar baixa"}
      </button>
    </Modal>
  );
}

export function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end lg:items-center justify-center p-0 lg:p-4" data-testid={testid}>
      <div className="bg-white w-full lg:max-w-md rounded-t-3xl lg:rounded-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-[#27272A]">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X size={20} className="text-[#71717A]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
export function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[#71717A] mb-1">{label}</label>
      {children}
    </div>
  );
}
